import type {
  Align,
  AnchorV,
  DesignBrief,
  DesignDirective,
  DesignPalette,
  DesignSpec,
  Decor,
  ImageAnalysis,
  LogoCorner,
  LogoSpec,
  Overlay,
  Rect,
  RecipeId,
  TextBlock,
  TextTransform,
  ZoneScore,
} from '@/types';
import {
  adjustLightness,
  compositeOver,
  contrastRatio,
  pickReadableText,
  toAccent,
} from '@/lib/color';
import { getFormat } from '@/lib/formats';
import { clamp, mulberry32, overlapRatio, shuffle, uid } from '@/lib/utils';
import {
  applyTransform,
  estimateWidth,
  pickEmphasisWords,
  safeBalance,
  type TextMeasurer,
} from '../typography/textOptimizer';
import { findZone, zoneColor } from '../analyzer/imageAnalyzer';
import { getStyle, type StylePreset } from './styles';

/**
 * The layout engine turns (image analysis + text + style + AI directive) into
 * a concrete DesignSpec. It owns all geometry; the AI only advises.
 *
 * Sequence, which is also the order a designer would work in:
 *   1. pick the text zone that avoids faces and busy detail
 *   2. derive a palette from the photo and decide light-on-dark or dark-on-light
 *   3. choose the minimum overlay treatment that reaches readable contrast
 *   4. set the type — size, weight, tracking, and balanced line breaks
 *   5. fit the block inside the zone and the format's safe area
 *   6. place supporting elements (rule, logo) where they don't collide
 */

const AA_CONTRAST = 4.5;
/** Headlines are large type, so the WCAG large-text threshold applies. */
const LARGE_TEXT_CONTRAST = 3.0;

export interface LayoutInput {
  analysis: ImageAnalysis;
  brief: DesignBrief;
  directive: DesignDirective;
  recipe: RecipeId;
  seed: number;
  name: string;
  /** Zones already used by sibling variants, to spread the set out. */
  takenZones?: ReadonlySet<string>;
  /**
   * Supplies real glyph metrics. Omit it and the engine falls back to its
   * built-in estimate, which keeps layout runnable without a DOM.
   */
  measureText?: (text: string, weight: number, transform: TextTransform) => number;
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

function buildPalette(
  analysis: ImageAnalysis,
  zone: ZoneScore,
  directive: DesignDirective,
): { palette: DesignPalette; rationale: string[] } {
  const rationale: string[] = [];
  const zoneBase = zoneColor(analysis, zone.rect);

  // The scheme follows the luminance of the region the text actually sits in,
  // not the whole frame — a dark photo can still have a bright sky where the
  // headline goes.
  let scheme: 'light' | 'dark' = zone.luminance < 0.52 ? 'light' : 'dark';
  if (directive.textColorScheme === 'light' || directive.textColorScheme === 'dark') {
    scheme = directive.textColorScheme;
  }

  // Luminance is a threshold; contrast is the thing that actually matters.
  // For a mid-tone region the threshold can land on the losing side, so verify
  // against the measured ratio and flip if the other choice is genuinely
  // better. The overlay planner will still add treatment on top of this.
  const check = pickReadableText(zoneBase, scheme);
  if (check.flipped) {
    scheme = scheme === 'light' ? 'dark' : 'light';
    rationale.push(
      `Mid-tone text area — ${scheme} type measured better against ${zoneBase} (${check.ratio.toFixed(1)}:1).`,
    );
  } else {
    rationale.push(
      scheme === 'light'
        ? `Text area reads dark (luminance ${zone.luminance.toFixed(2)}) — using light type.`
        : `Text area reads bright (luminance ${zone.luminance.toFixed(2)}) — using dark type.`,
    );
  }

  const primaryText = scheme === 'light' ? '#ffffff' : '#0f0f12';
  const secondaryText =
    scheme === 'light' ? adjustLightness(primaryText, -0.14) : adjustLightness(primaryText, 0.28);

  // Accent: the most saturated dominant color that isn't near-neutral, pulled
  // into a usable lightness band. Falls back to a tint of the primary so an
  // accent never fights the type.
  const candidates = analysis.dominantColors.filter((c) => {
    const r = parseInt(c.hex.slice(1, 3), 16);
    const g = parseInt(c.hex.slice(3, 5), 16);
    const b = parseInt(c.hex.slice(5, 7), 16);
    return Math.max(r, g, b) - Math.min(r, g, b) > 26;
  });
  const rawAccent = directive.accentColor || candidates[0]?.hex || analysis.dominantColors[0]?.hex || '#c8a882';
  const accent = toAccent(rawAccent, scheme);

  const overlay = scheme === 'light' ? '#07070a' : '#fbfbfd';

  rationale.push(`Accent ${accent} pulled from the photo's dominant colors.`);

  return {
    palette: {
      dominant: analysis.dominantColors.map((c) => c.hex),
      primaryText,
      secondaryText,
      accent,
      overlay,
      scheme,
    },
    rationale,
  };
}

// ---------------------------------------------------------------------------
// Overlay / readability
// ---------------------------------------------------------------------------

interface OverlayPlan {
  overlays: Overlay[];
  /** Effective background behind the text after the overlay is applied. */
  effectiveBackground: string;
  contrast: number;
  rationale: string[];
  treatment: DesignDirective['overlayTreatment'];
}

/**
 * Choose the lightest treatment that still clears the contrast threshold.
 *
 * We escalate rather than always reaching for a heavy scrim: none → scrim →
 * panel → blurred panel → full wash. Each step is only taken because the
 * previous one measurably failed, which is what keeps clean photos clean.
 */
function planOverlay(
  analysis: ImageAnalysis,
  zone: ZoneScore,
  palette: DesignPalette,
  style: StylePreset,
  directive: DesignDirective,
  recipe: RecipeId,
): OverlayPlan {
  const rationale: string[] = [];
  const zoneBase = zoneColor(analysis, zone.rect);
  const bare = contrastRatio(zoneBase, palette.primaryText);

  const wanted = directive.overlayTreatment;
  const strengthHint = clamp(
    directive.overlayStrength > 0 ? directive.overlayStrength : style.overlayStrength,
    0,
    0.95,
  );

  const overlays: Overlay[] = [];
  let treatment: DesignDirective['overlayTreatment'] = 'none';
  let effective = zoneBase;

  const needsHelp = bare < LARGE_TEXT_CONTRAST || analysis.busy || zone.detail > 0.13;

  if (!needsHelp && wanted === 'none') {
    rationale.push(
      `Photo is calm behind the text (contrast ${bare.toFixed(1)}:1) — no overlay needed.`,
    );
    return { overlays, effectiveBackground: effective, contrast: bare, rationale, treatment };
  }

  const useSolidPanel =
    wanted === 'panel' ||
    wanted === 'blur-panel' ||
    recipe === 'side-panel' ||
    recipe === 'split-band' ||
    (analysis.busy && zone.detail > 0.2);

  if (useSolidPanel) {
    const blur = wanted === 'blur-panel' || zone.detail > 0.24 ? 0.022 : 0;
    // Grow the panel past the text zone so the type gets real breathing room
    // inside it rather than sitting flush against the edge.
    const pad = 0.03 + style.airiness * 0.02;
    const rect: Rect = {
      x: clamp(zone.rect.x - pad, 0, 1),
      y: clamp(zone.rect.y - pad * 0.7, 0, 1),
      w: clamp(zone.rect.w + pad * 2, 0, 1),
      h: clamp(zone.rect.h + pad * 1.4, 0, 1),
    };
    let alpha = clamp(strengthHint * 0.95, 0.35, 0.92);
    effective = compositeOver(zoneBase, palette.overlay, alpha);

    // Escalate the panel opacity until the contrast target is met.
    let guard = 0;
    while (contrastRatio(effective, palette.primaryText) < AA_CONTRAST && alpha < 0.96 && guard < 24) {
      alpha += 0.04;
      effective = compositeOver(zoneBase, palette.overlay, alpha);
      guard++;
    }

    overlays.push({
      id: uid('ov'),
      kind: 'panel',
      rect,
      color: palette.overlay,
      opacity: alpha,
      blur,
      radius: style.id === 'bold' || style.id === 'editorial' ? 0 : 0.012,
      border:
        style.id === 'luxury' || style.id === 'minimal'
          ? { color: palette.primaryText, alpha: 0.22, width: 0.0012 }
          : null,
      visible: true,
    });
    treatment = blur > 0 ? 'blur-panel' : 'panel';
    rationale.push(
      blur > 0
        ? `Busy detail behind the text (${zone.detail.toFixed(2)}) — added a blurred panel at ${(alpha * 100) | 0}%.`
        : `Placed a ${(alpha * 100) | 0}% panel so the type sits on a stable field.`,
    );
  } else if (wanted === 'full-wash') {
    let alpha = clamp(strengthHint * 0.7, 0.2, 0.72);
    effective = compositeOver(zoneBase, palette.overlay, alpha);
    let guard = 0;
    while (contrastRatio(effective, palette.primaryText) < AA_CONTRAST && alpha < 0.85 && guard < 24) {
      alpha += 0.04;
      effective = compositeOver(zoneBase, palette.overlay, alpha);
      guard++;
    }
    overlays.push({
      id: uid('ov'),
      kind: 'solid',
      color: palette.overlay,
      opacity: alpha,
      visible: true,
    });
    treatment = 'full-wash';
    rationale.push(`Applied a full ${(alpha * 100) | 0}% wash for even contrast across the frame.`);
  } else {
    // Directional scrim from whichever edge the text hugs. This is the most
    // "photographic" option and the one a designer reaches for first.
    const cy = zone.rect.y + zone.rect.h / 2;
    const cx = zone.rect.x + zone.rect.w / 2;
    const vertical = Math.abs(cy - 0.5) >= Math.abs(cx - 0.5);
    const angle = vertical ? (cy > 0.5 ? 90 : 270) : cx > 0.5 ? 0 : 180;

    let alpha = clamp(strengthHint, 0.3, 0.92);
    // A scrim is graded, so the text sits nearer its dense end. Sample the
    // composite at ~85% strength rather than at the peak.
    effective = compositeOver(zoneBase, palette.overlay, alpha * 0.85);
    let guard = 0;
    while (contrastRatio(effective, palette.primaryText) < AA_CONTRAST && alpha < 0.95 && guard < 24) {
      alpha += 0.04;
      effective = compositeOver(zoneBase, palette.overlay, alpha * 0.85);
      guard++;
    }

    overlays.push({
      id: uid('ov'),
      kind: 'linear',
      angle,
      stops: [
        { at: 0, color: palette.overlay, alpha: 0 },
        { at: 0.42, color: palette.overlay, alpha: alpha * 0.34 },
        { at: 0.78, color: palette.overlay, alpha: alpha * 0.86 },
        { at: 1, color: palette.overlay, alpha },
      ],
      opacity: 1,
      visible: true,
    });
    treatment = 'scrim';
    rationale.push(
      `Graded scrim from the ${vertical ? (cy > 0.5 ? 'bottom' : 'top') : cx > 0.5 ? 'right' : 'left'} edge at ${(alpha * 100) | 0}% — keeps the photo readable without flattening it.`,
    );
  }

  const finalContrast = contrastRatio(effective, palette.primaryText);
  if (finalContrast < LARGE_TEXT_CONTRAST) {
    rationale.push(
      `Contrast is ${finalContrast.toFixed(1)}:1 after the overlay — a text shadow is carrying the rest.`,
    );
  }

  return { overlays, effectiveBackground: effective, contrast: finalContrast, rationale, treatment };
}

// ---------------------------------------------------------------------------
// Recipe geometry
// ---------------------------------------------------------------------------

interface RecipeGeometry {
  zoneId: string;
  align: Align;
  anchorV: 'top' | 'middle' | 'bottom';
  /** Text column width as a fraction of canvas width. */
  columnWidth: number;
  headlineScaleMul: number;
  maxLines: number;
  forceOverlay: DesignDirective['overlayTreatment'] | null;
}

/** Face coverage above this makes a zone unusable for a headline. */
const FACE_VETO = 0.12;

/**
 * Choose between a recipe's acceptable zones.
 *
 * A recipe expresses a composition, and its first candidate IS its identity —
 * a "lower third" that relocates to the top is no longer a lower third, and if
 * every recipe chases the highest-scoring zone all four variants collapse into
 * the same design. So the first choice is kept unless it is genuinely blocked:
 * a face over it, or a background so much worse that type would suffer.
 * Otherwise the recipe stays where it belongs.
 */
function chooseZone(
  analysis: ImageAnalysis,
  candidates: string[],
  taken: ReadonlySet<string> = new Set(),
): ZoneScore {
  const zones = candidates
    .map((id) => analysis.zones.find((z) => z.id === id))
    .filter((z): z is ZoneScore => z !== undefined);

  if (zones.length === 0) return analysis.zones[0];

  const pick = (pool: ZoneScore[]): ZoneScore | null => {
    if (pool.length === 0) return null;
    const first = pool[0];
    const blocked =
      first.faceOverlap > FACE_VETO ||
      // A markedly calmer alternative is worth moving for; a marginally better
      // one is not.
      pool.slice(1).some((z) => z.faceOverlap <= FACE_VETO && z.score > first.score + 0.3);
    if (!blocked) return first;
    return [...pool].sort((a, b) => a.faceOverlap - b.faceOverlap || b.score - a.score)[0];
  };

  // Priority order, strictest first. Face protection outranks variant
  // diversity: reusing a sibling's position is a cosmetic cost, putting a
  // headline across someone's face is not.
  const safe = zones.filter((z) => z.faceOverlap <= FACE_VETO);
  const safeAndFree = safe.filter((z) => !taken.has(z.id));

  const chosen =
    pick(safeAndFree) ?? // ideal: clear of faces and unclaimed
    pick(safe) ?? //        acceptable: clear of faces, shares a sibling's zone
    pick(zones); //         last resort: nothing is clear, take the least-bad

  return chosen ?? analysis.zones[0];
}

/** Vertical anchor implied by where a zone sits in the frame. */
function anchorFor(zone: ZoneScore): AnchorV {
  const centerY = zone.rect.y + zone.rect.h / 2;
  if (zone.rect.y < 0.12 && zone.rect.h < 0.5) return 'top';
  if (zone.rect.y + zone.rect.h > 0.88 && zone.rect.h < 0.5) return 'bottom';
  return centerY < 0.36 ? 'top' : centerY > 0.64 ? 'bottom' : 'middle';
}

function recipeGeometry(
  recipe: RecipeId,
  analysis: ImageAnalysis,
  directive: DesignDirective,
  style: StylePreset,
  taken: ReadonlySet<string>,
): RecipeGeometry {
  const preferred = directive.textZone || analysis.zones[0]?.id || 'bottom';
  const portrait = analysis.aspect < 1;

  // Fallbacks stay within the recipe's band before crossing the frame: a lower
  // third that has to move should try the bottom corners first and only then
  // go to the top. This keeps the composition recognisable and gives the
  // variant-diversity filter somewhere to go besides the opposite edge.
  const band = (side: 'top' | 'bottom') =>
    side === 'top'
      ? ['top', 'top-left', 'top-right']
      : ['bottom', 'bottom-left', 'bottom-right'];
  const first = preferred === 'top' ? 'top' : 'bottom';
  const second = first === 'top' ? 'bottom' : 'top';
  const vertical = [...band(first), ...band(second)];

  switch (recipe) {
    case 'editorial-stack': {
      const zone = chooseZone(analysis, vertical, taken);
      return {
        zoneId: zone.id,
        align: 'left',
        anchorV: anchorFor(zone),
        columnWidth: 0.8,
        headlineScaleMul: 1.14,
        maxLines: 4,
        forceOverlay: null,
      };
    }
    case 'centered-hero': {
      const zone = chooseZone(analysis, ['center', ...vertical], taken);
      return {
        zoneId: zone.id,
        align: 'center',
        anchorV: anchorFor(zone),
        columnWidth: 0.74 - style.airiness * 0.08,
        headlineScaleMul: 1.0,
        maxLines: 3,
        forceOverlay: null,
      };
    }
    case 'side-panel': {
      // Put the panel opposite the subject: if the person is right of center,
      // the text goes left. This is the single most useful composition rule
      // for portrait-led social graphics.
      const subjectCx = analysis.subject.x + analysis.subject.w / 2;
      const near = subjectCx > 0.5 ? 'left' : 'right';
      const far = near === 'left' ? 'right' : 'left';
      const zone = chooseZone(analysis, [near, far, ...vertical], taken);
      const lateral = zone.id === 'left' || zone.id === 'right';
      return {
        zoneId: zone.id,
        align: 'left',
        anchorV: anchorFor(zone),
        columnWidth: lateral ? 0.4 : 0.8,
        headlineScaleMul: lateral ? 0.78 : 1,
        maxLines: lateral ? 5 : 3,
        forceOverlay: 'panel',
      };
    }
    case 'top-kicker': {
      const zone = chooseZone(analysis, [...band('top'), ...band('bottom')], taken);
      return {
        zoneId: zone.id,
        align: 'left',
        anchorV: anchorFor(zone),
        columnWidth: 0.72,
        headlineScaleMul: 0.9,
        maxLines: 3,
        forceOverlay: null,
      };
    }
    case 'framed-minimal': {
      const zone = chooseZone(analysis, vertical, taken);
      return {
        zoneId: zone.id,
        align: 'center',
        anchorV: anchorFor(zone),
        columnWidth: 0.68,
        headlineScaleMul: 0.82,
        maxLines: 3,
        forceOverlay: 'none',
      };
    }
    case 'split-band': {
      const zone = chooseZone(
        analysis,
        portrait ? vertical : ['left', 'right', ...vertical],
        taken,
      );
      const lateral = zone.id === 'left' || zone.id === 'right';
      return {
        zoneId: zone.id,
        align: 'left',
        anchorV: anchorFor(zone),
        columnWidth: lateral ? 0.46 : 0.86,
        headlineScaleMul: 1.06,
        maxLines: 4,
        forceOverlay: 'panel',
      };
    }
    case 'corner-anchor': {
      // Start from the corner diagonally opposite the focal point, then let
      // the face check reorder if that corner is occupied anyway.
      const focalLeft = analysis.focalPoint.x > 0.5;
      const focalTop = analysis.focalPoint.y > 0.5;
      const opposite = `${focalTop ? 'top' : 'bottom'}-${focalLeft ? 'left' : 'right'}`;
      const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
      const zone = chooseZone(
        analysis,
        [opposite, ...corners.filter((c) => c !== opposite)],
        taken,
      );
      return {
        zoneId: zone.id,
        align: zone.id.endsWith('left') ? 'left' : 'right',
        anchorV: zone.id.startsWith('top') ? 'top' : 'bottom',
        columnWidth: 0.52,
        headlineScaleMul: 0.8,
        maxLines: 4,
        forceOverlay: null,
      };
    }
    case 'lower-third':
    default: {
      const zone = chooseZone(analysis, [...band('bottom'), ...band('top')], taken);
      return {
        zoneId: zone.id,
        align: 'left',
        anchorV: anchorFor(zone),
        columnWidth: 0.84,
        headlineScaleMul: 1.0,
        maxLines: 3,
        forceOverlay: null,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Type fitting
// ---------------------------------------------------------------------------

interface FittedText {
  lines: string[];
  fontSize: number;
}

/**
 * Convert a vertical measurement expressed in font-size units into a fraction
 * of canvas HEIGHT.
 *
 * fontSize is normalized to canvas WIDTH, so a line box of `leading * size`
 * occupies `leading * size * W` pixels; as a fraction of height that is
 * `leading * size * W / H` — i.e. multiplied by the aspect ratio, not divided
 * by it. Getting this backwards on a portrait canvas overstates the stack by
 * 1/aspect² and silently shrinks every headline, so it lives in one named
 * function rather than being inlined at each call site.
 */
function toHeightFraction(sizeUnits: number, aspectRatio: number): number {
  return sizeUnits * aspectRatio;
}

/**
 * Fit text to the column WIDTH only.
 *
 * Height is deliberately not considered here. Squeezing each block against its
 * own height budget destroys the type hierarchy — the headline and the
 * subtitle each get clipped by different amounts and the relationship between
 * them is lost. Instead every block is sized to the column, and `buildDesign`
 * applies one uniform scale to the whole stack if it overflows, which is what
 * a designer does when a block runs long.
 */
function fitTextToWidth(
  text: string,
  startSize: number,
  columnWidth: number,
  maxLines: number,
  tracking: number,
  measure: TextMeasurer,
): FittedText {
  let size = startSize;
  let lines = safeBalance(text, { maxLines, targetEmWidth: columnWidth / size, tracking, measure });

  for (let i = 0; i < 40; i++) {
    lines = safeBalance(text, { maxLines, targetEmWidth: columnWidth / size, tracking, measure });
    const widest = lines.reduce((m, l) => Math.max(m, measure(l, tracking)), 0);
    if (widest * size <= columnWidth * 1.002) break;
    size *= 0.96;
  }

  return { lines, fontSize: size };
}

/** Smallest sizes that stay legible at 1080px, as a fraction of canvas width. */
const MIN_SIZE: Record<'kicker' | 'headline' | 'subtitle', number> = {
  kicker: 0.013,
  headline: 0.034,
  subtitle: 0.016,
};

// ---------------------------------------------------------------------------
// Logo
// ---------------------------------------------------------------------------

function placeLogo(
  analysis: ImageAnalysis,
  textRect: Rect,
  style: StylePreset,
  directive: DesignDirective,
): { logo: LogoSpec; rationale: string } {
  const corners: { corner: LogoCorner; rect: Rect }[] = [
    { corner: 'top-left', rect: { x: 0.04, y: 0.03, w: 0.26, h: 0.12 } },
    { corner: 'top-right', rect: { x: 0.7, y: 0.03, w: 0.26, h: 0.12 } },
    { corner: 'bottom-left', rect: { x: 0.04, y: 0.85, w: 0.26, h: 0.12 } },
    { corner: 'bottom-right', rect: { x: 0.7, y: 0.85, w: 0.26, h: 0.12 } },
  ];

  const scored = corners.map(({ corner, rect }) => {
    const faceHit = analysis.faces.reduce((m, f) => Math.max(m, overlapRatio(rect, f)), 0);
    const subjectHit = overlapRatio(rect, analysis.subject);
    const textHit = overlapRatio(rect, textRect);
    const detail = findZone(analysis, corner)?.detail ?? 0.2;
    // Text collision is weighted hardest — a logo touching the headline is the
    // most visible failure of the three.
    const penalty = textHit * 3 + faceHit * 2.4 + subjectHit * 1.1 + detail * 1.4;
    return { corner, penalty };
  });

  scored.sort((a, b) => a.penalty - b.penalty);
  const chosen =
    directive.logoCorner && scored.some((s) => s.corner === directive.logoCorner && s.penalty < 0.5)
      ? directive.logoCorner
      : scored[0].corner;

  const padding = 0.045 + style.airiness * 0.018;
  const width = 0.14 + (1 - style.airiness) * 0.03;

  const isRight = chosen.endsWith('right');
  const isBottom = chosen.startsWith('bottom');

  return {
    logo: {
      corner: chosen,
      x: isRight ? 1 - padding - width : padding,
      y: isBottom ? 1 - padding : padding,
      freePosition: false,
      width,
      opacity: 0.95,
      padding,
      visible: true,
    },
    rationale: `Logo placed ${chosen.replace('-', ' ')} — the corner with the least overlap with the subject and headline.`,
  };
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

export function buildDesign(input: LayoutInput): DesignSpec {
  const { analysis, brief, directive, recipe, seed, name } = input;
  const style = getStyle(brief.styleId);
  const format = getFormat(brief.format);
  const aspect = format.width / format.height;
  const rand = mulberry32(seed);

  const measurerFor =
    (weight: number, transform: TextTransform): TextMeasurer =>
    (text, tracking) =>
      input.measureText
        ? input.measureText(applyTransform(text, transform), weight, transform) +
          tracking * text.length
        : estimateWidth(applyTransform(text, transform), tracking);

  const geo = recipeGeometry(
    recipe,
    analysis,
    directive,
    style,
    input.takenZones ?? new Set<string>(),
  );
  const zone = findZone(analysis, geo.zoneId);

  const { palette, rationale: paletteWhy } = buildPalette(analysis, zone, directive);

  const effectiveDirective: DesignDirective = {
    ...directive,
    overlayTreatment: geo.forceOverlay ?? directive.overlayTreatment,
  };
  const overlayPlan = planOverlay(analysis, zone, palette, style, effectiveDirective, recipe);

  const rationale = [
    `Text placed in the ${zone.label.toLowerCase()} (score ${zone.score.toFixed(2)}).`,
    ...(analysis.faces.length
      ? [
          `${analysis.faces.length} face${analysis.faces.length > 1 ? 's' : ''} detected (${analysis.faces[0].source}) — the headline avoids that area.`,
        ]
      : []),
    ...paletteWhy,
    ...overlayPlan.rationale,
  ];

  // ---- Type -------------------------------------------------------------
  const safeLeft = format.safe.left;
  const safeRight = 1 - format.safe.right;
  const usableWidth = safeRight - safeLeft;
  // The column must also fit the zone it was placed in — otherwise a
  // corner-anchored block spans the whole frame and stops being a corner
  // anchor (and can reach across a face the zone choice was avoiding).
  const columnWidth = Math.min(geo.columnWidth, usableWidth, zone.rect.w);

  const headlineTracking =
    directive.tracking !== 0 ? directive.tracking : style.headlineTracking;
  const headlineCase =
    directive.headlineCase === 'upper' || directive.headlineCase === 'lower'
      ? directive.headlineCase
      : style.headlineCase;
  const headlineWeight =
    directive.headlineWeight > 0 ? directive.headlineWeight : style.headlineWeight;

  // Uppercase eats roughly a fifth more width at the same point size.
  const caseWidthPenalty = headlineCase === 'upper' ? 1.18 : 1;

  // With a real measurer the case penalty is unnecessary — actual uppercase
  // metrics are already reflected in the measurement.
  const headlineColumn = input.measureText
    ? columnWidth
    : columnWidth / caseWidthPenalty;

  const headlineFit = fitTextToWidth(
    brief.title,
    style.headlineScale * geo.headlineScaleMul,
    headlineColumn,
    geo.maxLines,
    headlineTracking,
    measurerFor(headlineWeight, headlineCase),
  );

  const subtitleFit = brief.subtitle
    ? fitTextToWidth(
        brief.subtitle,
        style.subtitleScale,
        columnWidth,
        3,
        style.subtitleTracking,
        measurerFor(style.subtitleWeight, 'none'),
      )
    : null;

  const kickerFit = brief.kicker
    ? fitTextToWidth(
        brief.kicker,
        style.kickerScale,
        columnWidth,
        1,
        style.kickerTracking,
        measurerFor(style.kickerWeight, style.kickerCase),
      )
    : null;

  // ---- Stack layout ------------------------------------------------------
  // Everything below is a fraction of canvas HEIGHT.
  const measure = (
    headlineSize: number,
    subtitleSize: number,
    kickerSize: number,
  ) => {
    const gapAfterKicker = toHeightFraction(0.016 + style.airiness * 0.014, aspect);
    const gapAfterHeadline = toHeightFraction(0.018 + style.airiness * 0.018, aspect);
    const ruleGap = toHeightFraction(0.014 + style.airiness * 0.01, aspect);
    const kickerH = kickerFit ? toHeightFraction(kickerSize * 1.2, aspect) : 0;
    const headlineH = toHeightFraction(
      headlineFit.lines.length * style.headlineLeading * headlineSize,
      aspect,
    );
    const subtitleH = subtitleFit
      ? toHeightFraction(subtitleFit.lines.length * style.subtitleLeading * subtitleSize, aspect)
      : 0;
    const ruleH = style.useRule ? toHeightFraction(0.0035, aspect) : 0;
    const total =
      kickerH +
      (kickerFit ? gapAfterKicker : 0) +
      headlineH +
      (subtitleFit ? gapAfterHeadline + subtitleH : 0) +
      (style.useRule ? ruleGap + ruleH : 0);
    return { gapAfterKicker, gapAfterHeadline, ruleGap, kickerH, headlineH, subtitleH, ruleH, total };
  };

  // The stack may use its zone, plus whatever safe-area room lies beyond the
  // zone in the direction it grows — a bottom-anchored block can reach up.
  const safeHeight = 1 - format.safe.top - format.safe.bottom;
  const availableH =
    Math.min(
      Math.max(zone.rect.h, safeHeight * 0.52),
      safeHeight,
    ) * (1 - style.airiness * 0.14);

  let scale = 1;
  const natural = measure(headlineFit.fontSize, subtitleFit?.fontSize ?? 0, kickerFit?.fontSize ?? 0);
  if (natural.total > availableH) {
    // One uniform reduction keeps the size relationships between kicker,
    // headline and subtitle intact.
    scale = availableH / natural.total;
  }

  const headline = {
    lines: headlineFit.lines,
    fontSize: Math.max(MIN_SIZE.headline, headlineFit.fontSize * scale),
  };
  const subtitle = subtitleFit
    ? {
        lines: subtitleFit.lines,
        fontSize: Math.max(MIN_SIZE.subtitle, subtitleFit.fontSize * scale),
      }
    : null;
  const kicker = kickerFit
    ? {
        lines: kickerFit.lines,
        fontSize: Math.max(MIN_SIZE.kicker, kickerFit.fontSize * scale),
      }
    : null;

  const metrics = measure(
    headline.fontSize,
    subtitle?.fontSize ?? 0,
    kicker?.fontSize ?? 0,
  );
  const {
    gapAfterKicker,
    gapAfterHeadline,
    ruleGap,
    kickerH,
    headlineH,
    subtitleH,
    ruleH,
  } = metrics;
  const totalH = metrics.total;

  const safeTop = format.safe.top;
  const safeBottom = 1 - format.safe.bottom;

  let stackTop: number;
  if (geo.anchorV === 'top') {
    stackTop = Math.max(safeTop, zone.rect.y);
  } else if (geo.anchorV === 'bottom') {
    stackTop = Math.min(safeBottom, zone.rect.y + zone.rect.h) - totalH;
  } else {
    stackTop = zone.rect.y + (zone.rect.h - totalH) / 2;
  }
  stackTop = clamp(stackTop, safeTop, Math.max(safeTop, safeBottom - totalH));

  let anchorX: number;
  if (geo.align === 'center') anchorX = 0.5;
  else if (geo.align === 'right') anchorX = Math.min(safeRight, zone.rect.x + zone.rect.w);
  else anchorX = Math.max(safeLeft, zone.rect.x);

  const shadow =
    overlayPlan.contrast < AA_CONTRAST
      ? {
          blur: 0.018,
          x: 0,
          y: 0.002,
          color: palette.scheme === 'light' ? '#000000' : '#ffffff',
          alpha: clamp(0.75 - (overlayPlan.contrast - 1) * 0.12, 0.25, 0.8),
        }
      : palette.scheme === 'light'
        ? { blur: 0.01, x: 0, y: 0.0015, color: '#000000', alpha: 0.22 }
        : null;

  const blocks: TextBlock[] = [];
  const decor: Decor[] = [];
  let cursorY = stackTop;

  if (kicker) {
    blocks.push({
      id: uid('blk'),
      role: 'kicker',
      text: brief.kicker,
      lines: kicker.lines,
      autoLines: true,
      x: anchorX,
      y: cursorY,
      width: columnWidth,
      align: geo.align,
      anchorV: 'top',
      fontSize: kicker.fontSize,
      weight: style.kickerWeight,
      tracking: style.kickerTracking,
      lineHeight: 1.2,
      transform: style.kickerCase,
      color: palette.accent,
      opacity: 1,
      shadow,
      emphasis: null,
      visible: true,
      locked: false,
      fontFamily: '',
    });
    cursorY += kickerH + gapAfterKicker;
  }

  const emphasisWords =
    style.useEmphasis || directive.emphasisWords.length
      ? resolveEmphasis(brief.title, directive.emphasisWords)
      : [];

  blocks.push({
    id: uid('blk'),
    role: 'headline',
    text: brief.title,
    lines: headline.lines,
    autoLines: true,
    x: anchorX,
    y: cursorY,
    width: columnWidth,
    align: geo.align,
    anchorV: 'top',
    fontSize: headline.fontSize,
    weight: headlineWeight,
    tracking: headlineTracking,
    lineHeight: style.headlineLeading,
    transform: headlineCase,
    color: palette.primaryText,
    opacity: 1,
    shadow,
    emphasis: emphasisWords.length
      ? { words: emphasisWords, color: palette.accent, weight: headlineWeight }
      : null,
    visible: true,
    locked: false,
    fontFamily: '',
  });
  cursorY += headlineH;

  if (style.useRule) {
    cursorY += ruleGap;
    decor.push({
      id: uid('dec'),
      kind: 'rule',
      x: geo.align === 'center' ? 0.5 - 0.035 : geo.align === 'right' ? anchorX - 0.07 : anchorX,
      y: cursorY,
      length: 0.07,
      thickness: 0.0035,
      color: palette.accent,
      opacity: 1,
      visible: true,
    });
    cursorY += ruleH;
  }

  if (subtitle) {
    cursorY += gapAfterHeadline;
    blocks.push({
      id: uid('blk'),
      role: 'subtitle',
      text: brief.subtitle,
      lines: subtitle.lines,
      autoLines: true,
      x: anchorX,
      y: cursorY,
      width: columnWidth,
      align: geo.align,
      anchorV: 'top',
      fontSize: subtitle.fontSize,
      weight: style.subtitleWeight,
      tracking: style.subtitleTracking,
      lineHeight: style.subtitleLeading,
      transform: 'none',
      color: palette.secondaryText,
      opacity: 0.94,
      shadow,
      emphasis: null,
      visible: true,
      locked: false,
      fontFamily: '',
    });
    cursorY += subtitleH;
  }

  const textRect: Rect = {
    x: geo.align === 'center' ? anchorX - columnWidth / 2 : geo.align === 'right' ? anchorX - columnWidth : anchorX,
    y: stackTop,
    w: columnWidth,
    h: totalH,
  };

  let logo: LogoSpec | null = null;
  if (brief.hasLogo) {
    const placed = placeLogo(analysis, textRect, style, directive);
    logo = placed.logo;
    rationale.push(placed.rationale);
  }

  // A whisper of variation between regenerated versions — never enough to
  // break alignment, just enough that four options don't look like one.
  const jitter = (rand() - 0.5) * 0.004;

  rationale.push(
    `Helvetica Neue ${weightName(headlineWeight)} at ${(headline.fontSize * format.width).toFixed(0)}px, ${headline.lines.length} line${headline.lines.length > 1 ? 's' : ''}, ${headlineTracking >= 0 ? '+' : ''}${(headlineTracking * 1000).toFixed(0)} tracking.`,
  );

  return {
    id: uid('design'),
    name,
    recipe,
    zoneId: zone.id,
    styleId: brief.styleId,
    format: brief.format,
    image: {
      brightness: style.imageBrightness,
      contrast: style.imageContrast,
      saturation: style.imageSaturation,
      blur: 0,
      scale: 1,
      offsetX: 0,
      offsetY: 0,
    },
    overlays: overlayPlan.overlays,
    blocks: blocks.map((b) => ({ ...b, y: b.y + jitter })),
    decor,
    logo,
    palette,
    rationale,
  };
}

function weightName(weight: number): string {
  if (weight <= 250) return 'UltraLight';
  if (weight <= 350) return 'Light';
  if (weight <= 450) return 'Regular';
  if (weight <= 550) return 'Medium';
  if (weight <= 750) return 'Bold';
  return 'Heavy';
}

/** Map AI-suggested words back to indices, falling back to the local heuristic. */
function resolveEmphasis(title: string, suggested: string[]): number[] {
  const words = title.trim().split(/\s+/);
  if (suggested.length) {
    const wanted = new Set(suggested.map((w) => w.toLowerCase().replace(/[^a-z0-9']/g, '')));
    const hits = words
      .map((w, i) => ({ w: w.toLowerCase().replace(/[^a-z0-9']/g, ''), i }))
      .filter(({ w }) => wanted.has(w))
      .map(({ i }) => i);
    if (hits.length) return hits.slice(0, 3);
  }
  return pickEmphasisWords(title, 2);
}

/**
 * Produce N distinct compositions. Each uses a different recipe so the four
 * options differ in placement and structure, not just in colour.
 */
export function buildVariants(
  analysis: ImageAnalysis,
  brief: DesignBrief,
  directive: DesignDirective,
  count: number,
  seed: number,
  measureText?: LayoutInput['measureText'],
): DesignSpec[] {
  const style = getStyle(brief.styleId);
  const rand = mulberry32(seed);

  // Start from the AI's ranked recipes, top up from the style's own list, and
  // shuffle the tail so a re-roll reorders without losing the best option.
  const ordered: RecipeId[] = [];
  for (const r of [...directive.recipes, ...style.recipes]) {
    if (!ordered.includes(r)) ordered.push(r);
  }
  const head = ordered.slice(0, 1);
  const tail = shuffle(ordered.slice(1), rand);
  const chosen = [...head, ...tail].slice(0, count);

  const names = ['Minimal type', 'Bold editorial', 'Premium', 'Modern social', 'Alternate'];

  // Build sequentially, telling each variant where its siblings already sit.
  // Without this, a photo with one obviously-best zone produces four designs
  // that differ only in typography — which is not four options.
  const takenZones = new Set<string>();
  const designs: DesignSpec[] = [];

  for (let i = 0; i < chosen.length; i++) {
    const design = buildDesign({
      analysis,
      brief,
      directive,
      recipe: chosen[i],
      seed: seed + i * 977,
      name: names[i] ?? `Option ${i + 1}`,
      takenZones,
      measureText,
    });
    takenZones.add(design.zoneId);
    designs.push(design);
  }

  return designs;
}

/** Re-run the engine for a new format while keeping the recipe and style. */
export function reflow(
  spec: DesignSpec,
  analysis: ImageAnalysis,
  brief: DesignBrief,
  directive: DesignDirective,
  measureText?: LayoutInput['measureText'],
): DesignSpec {
  const rebuilt = buildDesign({
    analysis,
    brief,
    directive,
    recipe: spec.recipe,
    seed: 1,
    name: spec.name,
    measureText,
  });
  // Preserve the user's photo adjustments across a format change.
  return { ...rebuilt, id: spec.id, image: spec.image };
}
